import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Button, Checkbox, Divider, FormControl, FormHelperText, FormLabel, HStack, Input, Select, Slider, SliderFilledTrack, SliderMark, SliderThumb, SliderTrack, Stack, Text, Textarea } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchCompany, getUser, updateCompany, updateDiscountRate, updateReferalRate, updateVatOption } from '../../store/actions';
import { useAppDispatch, useAppSelector } from '../../hooks';

import { AppDispatch } from '../../store';
import { ToastContainer } from 'react-toastify';
import { updateBankingFormValidation } from '../../utils';
import { useFormik } from 'formik';

const labelStyles = {
    mt: '2',
    ml: '-2.5',
    fontSize: 'sm',
};

export const BankingSettings = () => {

    const [isVatRegistered, setVatRegistered] = useState<boolean>(false);
    const [isUpdatingVatOption, setUpdatingVatOption] = useState<boolean>(false);
    const [referalRate, setReferalRate] = useState<number>(0);
    const [isUpdatingReferalRate, setUpdatingReferalRate] = useState<boolean>(false);
    const [isUpdatingDiscountRate, setUpdatingDiscountRate] = useState<boolean>(false);
    const [discountRate, setDiscountRate] = useState<number>(0);
    const [sliderValue, setSliderValue] = useState(50);

    const { company, is_updating_company } = useAppSelector(state => state.companyReducer);

    const { company_id } = company ?? {};

    const dispatch: AppDispatch = useAppDispatch();

    const updateBankingForm = useFormik({
        initialValues: {
            bank_name: '',
            account_number: '',
            note: '',
            terms_of_service: '',
        },
        validationSchema: updateBankingFormValidation,
        validateOnBlur: true,
        onSubmit: (values: any) => {
            const newValues = {
                ...company,
                settings: {
                    payment: {
                        bank: {
                            account_number: values.account_number,
                            bank_name: values.bank_name,
                        },
                        note: values.note,
                        terms_of_service: values.terms_of_service,
                    }
                }
            };
            dispatch(updateCompany(`${company._id}`, newValues));
            // alert(JSON.stringify(newValues, null, 2));
        }
    });

    const handleUpdateVatOptionUpdate = () => {
        setUpdatingVatOption(true);
        dispatch(updateVatOption({
            company_id,
            vat: isVatRegistered
        }))
            .finally(() => setUpdatingVatOption(false));
    };

    const handleUpdateReferalRateUpdate = () => {
        setUpdatingReferalRate(true);
        dispatch(updateReferalRate({
            company_id,
            referal_rate: referalRate
        }))
            .finally(() => setUpdatingReferalRate(false));
    };
    const handleUpdateDiscountRateUpdate = () => {
        setUpdatingDiscountRate(true);
        dispatch(updateDiscountRate({
            company_id,
            discount_percentage: discountRate
        }))
            .finally(() => setUpdatingDiscountRate(false));
    };

    const setDefaultValues = useCallback(() => {
        updateBankingForm.setFieldValue('bank_name', company?.settings?.payment?.bank?.bank_name);
        updateBankingForm.setFieldValue('account_number', company?.settings?.payment?.bank?.account_number);
        updateBankingForm.setFieldValue('note', company?.settings?.payment?.note);
        updateBankingForm.setFieldValue('terms_of_service', company?.settings?.payment?.terms_of_service);
        setVatRegistered(Boolean(company?.settings?.payment?.vat));
    }, []);

    useEffect(() => { }, [updateBankingForm.values, is_updating_company]);

    useEffect(() => {

        dispatch(getUser());
        dispatch(fetchCompany());
        setVatRegistered(Boolean(company?.settings?.payment?.vat));
        setReferalRate(Number(company?.referal_rate));
        setDiscountRate(Number(company?.discount_percentage * 100));

        return () => {
            setDefaultValues();
        };
    }, []);

    return (
        <Stack bg={'#FFFFFF'} p={6} borderRadius={6} >
            <Stack>
                <HStack justifyContent={'space-between'}>
                    <Text textTransform={'capitalize'} fontWeight={'semibold'} fontSize={'1.5rem'} >Update billing details</Text>
                </HStack>
                <Divider pt={2} />
            </Stack>
            <Stack pt={4}>
                <Accordion defaultIndex={[0, 1, 2, 3]} allowMultiple>
                    <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'}>
                        <h2>
                            <AccordionButton bg={'gray.50'} minH={'50px'}>
                                <Box as='span' flex='1' textAlign='left' fontWeight={'medium'}>
                                    Banking Details
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                            <form onSubmit={updateBankingForm.handleSubmit}>
                                <HStack>
                                    <Box>
                                        <FormControl id='bank_name' isRequired mb={updateBankingForm.errors.bank_name ? 0 : 5}>
                                            <FormLabel>Bank</FormLabel>
                                            <Select name='bank_name' value={updateBankingForm.values.bank_name} onChange={updateBankingForm.handleChange} onBlur={updateBankingForm.handleBlur} >
                                                <option value='fnb'>FNB</option>
                                                <option value='absa'>ABSA</option>
                                                <option value='capitec'>Capitec</option>
                                                <option value='standard bank'>Standard Bank</option>
                                                <option value='nedbank'>Nedbank</option>
                                                <option value='old mutual'>Old Mutual</option>
                                            </Select>
                                            {updateBankingForm.errors.bank_name && <Text fontSize='sm' color='red.400'>{updateBankingForm.errors.bank_name}</Text>}
                                        </FormControl>
                                    </Box>
                                    <Box>
                                        <FormControl id='account_number' isRequired mb={updateBankingForm.errors.account_number ? 0 : 5}>
                                            <FormLabel>Account number</FormLabel>
                                            <Input name='account_number' type="text" value={updateBankingForm.values.account_number} onChange={updateBankingForm.handleChange} onBlur={updateBankingForm.handleBlur} />
                                            {updateBankingForm.errors.account_number && <Text fontSize='sm' color='red.400'>{updateBankingForm.errors.account_number}</Text>}
                                        </FormControl>
                                    </Box>
                                </HStack>
                                <FormControl id='note' isRequired mb={updateBankingForm.errors.note ? 2 : 5}>
                                    <FormLabel>Note</FormLabel>
                                    <Textarea name='note' placeholder='Note to show on invoice' value={updateBankingForm.values.note} onChange={updateBankingForm.handleChange} onBlur={updateBankingForm.handleBlur} />
                                    <FormHelperText>Note to show on invoice.</FormHelperText>
                                </FormControl>
                                <FormControl id='terms_of_service' isRequired mb={updateBankingForm.errors.note ? 0 : 5}>
                                    <FormLabel>Terms of service</FormLabel>
                                    <Textarea name='terms_of_service' placeholder='Note to show on invoice' value={updateBankingForm.values.terms_of_service} onChange={updateBankingForm.handleChange} onBlur={updateBankingForm.handleBlur} />
                                    <FormHelperText>Terms and conditions of payment.</FormHelperText>
                                </FormControl>
                                <Stack spacing={10} pt={2}>
                                    <Button
                                        type='submit'
                                        isLoading={is_updating_company}
                                        isDisabled={is_updating_company || !updateBankingForm?.isValid}
                                        size="lg"
                                        colorScheme={'yellow'}
                                    >
                                        Submit
                                    </Button>
                                </Stack>
                            </form>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'} mt={4}>
                        <h2>
                            <AccordionButton bg={'gray.50'} minH={'50px'}>
                                <Box as='span' flex='1' textAlign='left' fontWeight={'medium'}>
                                    VAT Option
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                            <HStack justifyContent={'space-between'}>
                                <Stack>
                                    <Text fontSize={'sm'} color={'gray.900'} mt={0} fontWeight={'medium'}>Registered for VAT?</Text>
                                    <Checkbox colorScheme={'yellow'} mt={'2px !important'} isChecked={isVatRegistered} onChange={() => setVatRegistered(!isVatRegistered)}>Yes</Checkbox>
                                    <Text fontSize={'xs'} color={'gray.500'} mt={'0px !important'}>Yes, if your business is registered for VAT (15%).</Text>
                                </Stack>
                                <Button
                                    size={'sm'}
                                    isLoading={isUpdatingVatOption}
                                    isDisabled={isUpdatingVatOption}
                                    colorScheme={'yellow'}
                                    onClick={handleUpdateVatOptionUpdate}
                                >Update</Button>
                            </HStack>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'} mt={4}>
                        <h2>
                            <AccordionButton bg={'gray.50'} minH={'50px'}>
                                <Box as='span' flex='1' textAlign='left' fontWeight={'medium'}>
                                    Referal rate
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                            <HStack justifyContent={'space-between'}>
                                <Stack>
                                    <Text fontSize={'sm'} color={'gray.900'} mt={0} fontWeight={'medium'}>Referal rate value(%)?</Text>
                                    <Input
                                        type={'number'}
                                        value={referalRate}
                                        onChange={(e: any) => setReferalRate(e.target.value)}
                                        colorScheme={'yellow'}
                                    />
                                    <Text fontSize={'xs'} color={'gray.500'} mt={'0px !important'}>If you have agent who refer deliveries for you, in percentage (%).</Text>
                                </Stack>
                                <Button
                                    size={'sm'}
                                    isLoading={isUpdatingReferalRate}
                                    isDisabled={isUpdatingReferalRate}
                                    colorScheme={'yellow'}
                                    onClick={handleUpdateReferalRateUpdate}
                                >Update</Button>
                            </HStack>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'} mt={4}>
                        <h2>
                            <AccordionButton bg={'gray.50'} minH={'50px'}>
                                <Box as='span' flex='1' textAlign='left' fontWeight={'medium'}>
                                    Discount rate
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                            <HStack justifyContent={'space-between'}>
                                <Stack>
                                    <Text fontSize={'sm'} color={'gray.900'} mt={0} fontWeight={'medium'}>Discount rate value(%)?</Text>
                                    <Box pt={6} pb={2}>
                                        <Slider aria-label='slider-ex-6' onChange={(val) => setDiscountRate(val)} colorScheme={'yellow'}>
                                            <SliderMark
                                                value={discountRate}
                                                textAlign='center'
                                                bg='yellow.500'
                                                color='white'
                                                mt='-10'
                                                ml='-5'
                                                w='12'
                                                borderRadius={4}
                                            >
                                                {discountRate}%
                                            </SliderMark>
                                            <SliderTrack>
                                                <SliderFilledTrack />
                                            </SliderTrack>
                                            <SliderThumb />
                                        </Slider>
                                    </Box>
                                    <Text fontSize={'xs'} color={'gray.500'} mt={'0px !important'}>Give delivery discount to your customers, in percentage (%).</Text>
                                </Stack>
                                <Button
                                    size={'sm'}
                                    isLoading={isUpdatingDiscountRate}
                                    isDisabled={isUpdatingDiscountRate}
                                    colorScheme={'yellow'}
                                    onClick={handleUpdateDiscountRateUpdate}
                                >Update</Button>
                            </HStack>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>

            </Stack>
            <ToastContainer />
        </Stack>
    );
};